import { prefetchComponent } from '@/scripts/helpers/vue-helper';
import { CreateElement } from 'vue';

export interface InPlayerOverlayManifestCreationEntry<K extends string = string> {
  name: K;
  component: Parameters<CreateElement>[0];
  /**
   * higher number signify higher precedence.
   * An overlay with higher precedence will be displayed on top of overlays with lower precedence.
   * If the overlay does not overlay each other, it will be displayed together
   */
  precedence: number;
  position: string;
}

export interface InPlayerOverlayManifest<K extends string = string> extends Omit<InPlayerOverlayManifestCreationEntry<K>, 'position'> {
  position: number;
}

const normalizeManifestEntryPosition = (entryPosition: InPlayerOverlayManifestCreationEntry['position']): number => (
  parseInt(entryPosition.replace(/\s/g, ''), 2)
);

const createManifests = <K extends string>(arr: (InPlayerOverlayManifestCreationEntry<K>)[]): { [key in K]: InPlayerOverlayManifest<K> } => (
  Object.fromEntries(
    arr.map(({ name, ...entry }) => [
      name,
      {
        name,
        ...entry,
        position: normalizeManifestEntryPosition(entry.position),
      },
    ]),
  ) as any
);

const manifests = createManifests([
  {
    name: 'WatchNextOverlay',
    // Load component asynchronously to prevent cyclic dependency issue
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    component: () => import(
      /* webpackPrefetch: true */
      // eslint-disable-next-line comma-dangle
      './WatchNextOverlay/WatchNextOverlay.ts.vue'
    ),
    position: `
      000
      001
      000
    `,
    precedence: 1,
  },
  {
    name: 'FilmReviewHint',
    component: prefetchComponent(() => import('./FilmReviewOverlays/index').then(({ FilmReviewHintPresenter }) => ({ default: FilmReviewHintPresenter }))),
    position: `
      001
      000
      000
    `,
    precedence: 1,
  },
  {
    name: 'DownloadApp',
    component: prefetchComponent(() => import('./FilmReviewOverlays/index').then(({ DownloadApp }) => ({ default: DownloadApp }))),
    position: `
    000
    000
    010
    `,
    precedence: 1,
  },
  {
    name: 'FilmReviewQuestions',
    component: prefetchComponent(() => import('./FilmReviewOverlays/index').then(({ FilmReviewQuestionsPresenter }) => ({ default: FilmReviewQuestionsPresenter }))),
    position: `
      111
      111
      111
    `,
    precedence: 2,
  },
]);

export type InPlayerOverlayKey = keyof typeof manifests;

export const getManifestByName = <K extends InPlayerOverlayKey>(key: K): InPlayerOverlayManifest<K> => {
  const item = manifests[key];

  if (!item) throw new Error('No manifest matched the given key');

  return item as InPlayerOverlayManifest<K>;
};
